import { useState, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui'
import { Input } from '@/components/ui'
import { Select } from '@/components/ui'
import { createBrainWorkOrder, createBrainWorkOrderFromAudio } from '@/api/brain'

interface WorkOrderComposeProps {
  onClose: () => void
  onCreated: () => void
  brainOptions?: Array<{ value: string; label: string }>
}

const DEFAULT_BRAINS = [
  { value: 'decisive-gm', label: 'Decisive GM' },
  { value: 'mech-browse-001', label: 'ThinkBrowse' },
]

const PRIORITIES = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
]

export function WorkOrderCompose({ onClose, onCreated, brainOptions }: WorkOrderComposeProps) {
  const [mode, setMode] = useState<'manual' | 'audio'>('manual')
  const [subject, setSubject] = useState('')
  const [instructions, setInstructions] = useState('')
  const [priority, setPriority] = useState('medium')
  const [targetBrain, setTargetBrain] = useState('decisive-gm')
  const [audioFile, setAudioFile] = useState<File | null>(null)
  const [recording, setRecording] = useState(false)
  const [recordingSupported, setRecordingSupported] = useState(false)
  const [audioResultMessage, setAudioResultMessage] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null)
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null)
  const resolvedBrainOptions = brainOptions && brainOptions.length > 0 ? brainOptions : DEFAULT_BRAINS

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    },
    [onClose],
  )

  useEffect(() => {
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [handleEscape])

  useEffect(() => {
    setRecordingSupported(
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia &&
      typeof MediaRecorder !== 'undefined',
    )
  }, [])

  useEffect(() => {
    if (!success) return
    const timer = setTimeout(() => {
      onCreated()
      onClose()
    }, 800)
    return () => clearTimeout(timer)
  }, [success, onCreated, onClose])

  useEffect(() => {
    return () => {
      mediaRecorder?.stop()
      mediaStream?.getTracks().forEach((track) => track.stop())
    }
  }, [mediaRecorder, mediaStream])

  useEffect(() => {
    const hasSelectedTarget = resolvedBrainOptions.some((option) => option.value === targetBrain)
    if (!hasSelectedTarget && resolvedBrainOptions.length > 0) {
      setTargetBrain(resolvedBrainOptions[0].value)
    }
  }, [resolvedBrainOptions, targetBrain])

  const handleSubmit = async () => {
    setError(null)
    setAudioResultMessage(null)
    setSubmitting(true)

    try {
      await createBrainWorkOrder({
        from: 'founder',
        to: targetBrain,
        subject,
        body: { instructions },
        priority: priority as 'low' | 'medium' | 'high' | 'critical',
      })
      setSuccess(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create work order')
    } finally {
      setSubmitting(false)
    }
  }

  const startRecording = async () => {
    setError(null)
    setAudioResultMessage(null)
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const recorder = new MediaRecorder(stream)
      const chunks: BlobPart[] = []

      recorder.ondataavailable = (event: BlobEvent) => {
        if (event.data.size > 0) chunks.push(event.data)
      }

      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: recorder.mimeType || 'audio/webm' })
        const recorded = new File([blob], `voice-note-${Date.now()}.webm`, { type: blob.type })
        setAudioFile(recorded)
      }

      recorder.start()
      setMediaStream(stream)
      setMediaRecorder(recorder)
      setRecording(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Microphone access failed')
    }
  }

  const stopRecording = () => {
    mediaRecorder?.stop()
    mediaStream?.getTracks().forEach((track) => track.stop())
    setMediaRecorder(null)
    setMediaStream(null)
    setRecording(false)
  }

  const handleAudioSubmit = async () => {
    if (!audioFile) return
    setError(null)
    setAudioResultMessage(null)
    setSubmitting(true)

    try {
      const result = await createBrainWorkOrderFromAudio({
        audio: audioFile,
        to: targetBrain,
        priority: priority as 'low' | 'medium' | 'high' | 'critical',
      })
      if (result.created) {
        setAudioResultMessage(
          result.degraded
            ? 'Audio uploaded. Work order created with manual-processing fallback.'
            : 'Audio processed and work order created.',
        )
        setSuccess(true)
      } else {
        setAudioResultMessage('Audio processed as PERSONAL. No work order created.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process audio')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-swiss-black/40"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Create work order"
    >
      <div
        className="w-full max-w-[480px] max-h-[90vh] overflow-y-auto bg-swiss-white border border-swiss-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-swiss-gray-200">
          <h2 className="text-swiss-h2 font-swiss-sans font-semibold">
            New Work Order
          </h2>
          <button
            onClick={onClose}
            className="text-swiss-gray-500 hover:text-swiss-black transition-colors duration-150 cursor-pointer p-1"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path
                d="M4 4L12 12M12 4L4 12"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="square"
              />
            </svg>
          </button>
        </div>

        {error && (
          <div className="text-swiss-red text-swiss-small px-6 pt-4">
            {error}
          </div>
        )}

        {success && (
          <div className="text-swiss-green text-swiss-small px-6 pt-4">
            Work order created successfully.
          </div>
        )}

        <div className="px-6 py-4 flex flex-col gap-4">
          <div className="flex border border-swiss-gray-200">
            <button
              type="button"
              onClick={() => setMode('manual')}
              className={`flex-1 px-3 py-2 text-swiss-small cursor-pointer ${mode === 'manual' ? 'bg-swiss-black text-swiss-white' : 'bg-swiss-white text-swiss-black'}`}
            >
              Manual
            </button>
            <button
              type="button"
              onClick={() => setMode('audio')}
              className={`flex-1 px-3 py-2 text-swiss-small cursor-pointer ${mode === 'audio' ? 'bg-swiss-black text-swiss-white' : 'bg-swiss-white text-swiss-black'}`}
            >
              Audio
            </button>
          </div>

          {mode === 'manual' ? (
            <>
              <Input
                label="Subject"
                required
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Brief description of work"
              />

              <div className="flex flex-col gap-1">
                <label
                  htmlFor="wo-instructions"
                  className="text-[11px] uppercase tracking-[0.1em] font-medium text-swiss-gray-500"
                >
                  Instructions
                </label>
                <textarea
                  id="wo-instructions"
                  className="w-full min-h-[120px] resize-none border border-swiss-gray-200 p-3 text-swiss-body font-swiss-sans focus:outline-none focus:border-swiss-black transition-colors duration-150"
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Detailed instructions for the agent..."
                />
              </div>
            </>
          ) : (
            <>
              <div className="flex flex-col gap-2">
                <label className="text-[11px] uppercase tracking-[0.1em] font-medium text-swiss-gray-500">
                  Audio File
                </label>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null
                    setAudioFile(file)
                    setAudioResultMessage(null)
                  }}
                  className="border border-swiss-gray-200 p-2 text-swiss-small"
                />
                {recordingSupported && (
                  <div className="flex gap-2">
                    {!recording ? (
                      <Button variant="secondary" onClick={startRecording}>
                        Start Recording
                      </Button>
                    ) : (
                      <Button variant="secondary" onClick={stopRecording}>
                        Stop Recording
                      </Button>
                    )}
                  </div>
                )}
                {audioFile && (
                  <div className="text-swiss-small text-swiss-gray-500">
                    Selected: {audioFile.name} ({(audioFile.size / 1024 / 1024).toFixed(2)} MB)
                  </div>
                )}
                {audioResultMessage && (
                  <div className="text-swiss-small text-swiss-gray-500">{audioResultMessage}</div>
                )}
              </div>
            </>
          )}

          <Select
            label="Target Brain"
            options={resolvedBrainOptions}
            value={targetBrain}
            onChange={(e) => setTargetBrain(e.target.value)}
          />

          <Select
            label="Priority"
            options={PRIORITIES}
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
          />
        </div>

        <div className="px-6 py-4 border-t border-swiss-gray-200 flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {mode === 'manual' ? (
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={submitting || !subject.trim() || !instructions.trim()}
            >
              {submitting ? 'Creating...' : 'Create'}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleAudioSubmit}
              disabled={submitting || !audioFile}
            >
              {submitting ? 'Processing...' : 'Create From Audio'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}
